import { parsePlace, samePlace, type Place } from '@guildite/domain/places';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { useCurrentUser, useData } from '../../data/DataContext.tsx';
import { LocationError, roughPosition } from '../../platform/geolocation.ts';
import { useMaps } from '../../platform/maps/MapsContext.tsx';

// Where the app thinks you are, like food apps and Instagram/TikTok:
// - saved: the town in Settings → Location (private, on your account);
// - detected: the town this device is in, found each time the app opens when
//   "Use my current location" is on (kept on this device only);
// - browsing: another city picked to look around, for this visit only.
// Only a town and a country code are ever kept: the device's position is
// turned into a town and dropped.
export type FindStatus = 'off' | 'finding' | 'found' | 'denied' | 'failed';

export type Whereabouts = {
  saved: Place | null;
  detected: Place | null;
  follow: boolean;
  status: FindStatus;
  browsing: Place | null;
  // Where you really are: the detected town, else the saved one.
  here: Place | null;
  // What "near you", the map and local posts use: browsing, else here.
  active: Place | null;
  setFollow(on: boolean): void;
  browse(place: Place | null): void;
  // After Settings → Location saves a new town.
  savedChanged(place: Place | null): void;
};

export const FOLLOW_KEY = 'guildite.followLocation';
export const LAST_TOWN_KEY = 'guildite.lastTown';
export const BROWSE_KEY = 'guildite.browsePlace';

function read(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function write(storage: () => Storage, key: string, value: string | null) {
  try {
    if (value === null) storage().removeItem(key);
    else storage().setItem(key, value);
  } catch {
    // Not remembered on this device.
  }
}

function readPlace(storage: () => Storage, key: string): Place | null {
  try {
    return parsePlace(JSON.parse(read(storage, key) ?? 'null'));
  } catch {
    return null;
  }
}

const local = () => localStorage;
const session = () => sessionStorage;

const LocationContext = createContext<Whereabouts | null>(null);

export function LocationProvider({ children }: { children: ReactNode }) {
  const { account } = useData();
  const maps = useMaps();
  const user = useCurrentUser();
  const [savedState, setSaved] = useState<{ uid: string; place: Place | null } | null>(null);
  const saved = user && savedState?.uid === user.uid ? savedState.place : null;
  const [follow, setFollowState] = useState(() => read(local, FOLLOW_KEY) === 'on');
  const [detected, setDetected] = useState<Place | null>(() => (read(local, FOLLOW_KEY) === 'on' ? readPlace(local, LAST_TOWN_KEY) : null));
  const [status, setStatus] = useState<FindStatus>(follow ? 'finding' : 'off');
  const [browsing, setBrowsing] = useState<Place | null>(() => readPlace(session, BROWSE_KEY));
  const finding = useRef(0);

  useEffect(() => {
    if (!user) return;
    let active = true;
    account.privateDetails().then(details => { if (active) setSaved({ uid: user.uid, place: parsePlace(details) }); }, () => undefined);
    return () => { active = false; };
  }, [account, user]);

  // Callers set the status to 'finding' first (it starts that way when on).
  const find = useCallback(() => {
    const attempt = ++finding.current;
    roughPosition()
      .then(point => maps.townAt(point))
      .then(place => {
        if (attempt !== finding.current) return;
        if (!place) {
          setStatus('failed');
          return;
        }
        setDetected(place);
        setStatus('found');
        write(local, LAST_TOWN_KEY, JSON.stringify(place));
      }, (error: unknown) => {
        if (attempt !== finding.current) return;
        setStatus(error instanceof LocationError && error.reason === 'denied' ? 'denied' : 'failed');
        if (error instanceof LocationError && error.reason === 'denied') setDetected(null);
      });
  }, [maps]);

  // Each time the app opens with "Use my current location" on.
  useEffect(() => {
    if (follow) find();
    // Only on open; turning the switch on calls find itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFollow = useCallback((on: boolean) => {
    setFollowState(on);
    write(local, FOLLOW_KEY, on ? 'on' : null);
    if (on) {
      setStatus('finding');
      find();
    } else {
      finding.current += 1;
      setDetected(null);
      setStatus('off');
      write(local, LAST_TOWN_KEY, null);
    }
  }, [find]);

  const browse = useCallback((place: Place | null) => {
    setBrowsing(place);
    write(session, BROWSE_KEY, place ? JSON.stringify(place) : null);
  }, []);

  const value = useMemo<Whereabouts>(() => {
    const here = (follow ? detected : null) ?? saved;
    const away = browsing && !samePlace(browsing, here) ? browsing : null;
    const savedChanged = (place: Place | null) => { if (user) setSaved({ uid: user.uid, place }); };
    return { saved, detected: follow ? detected : null, follow, status, browsing: away, here, active: away ?? here, setFollow, browse, savedChanged };
  }, [saved, detected, follow, status, browsing, setFollow, browse, user]);

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
}

const NOWHERE: Whereabouts = {
  saved: null, detected: null, follow: false, status: 'off', browsing: null, here: null, active: null,
  setFollow: () => undefined, browse: () => undefined, savedChanged: () => undefined,
};

export function useWhereabouts(): Whereabouts {
  return useContext(LocationContext) ?? NOWHERE;
}
